/**
 * Procedurele oppervlaktexturen.
 *
 * Waarom gerekend en niet gedownload: deze kiosk draait offline op een mini-pc
 * op een beursvloer. Elke megabyte textuur is een megabyte die op 3 november om
 * 08:55 uur binnen moet zijn. Een set van vier PBR-materialen in 2K kost al gauw
 * 40 MB; dit bestand kost 9 kB en rekent dezelfde maps uit bij het laden, achter
 * het laadscherm dat er toch al staat.
 *
 * Wat het oplost: tot nu toe was élk oppervlak in de hal één vlakke kleur —
 * `meshStandardMaterial color="#1d2026"` op 1.200 m² beton. Dat is de tweede
 * grote oorzaak van "het leest als blokken", na het ontbreken van AO (dat in
 * Effecten.tsx al is opgelost). Een vlak zonder variatie geeft het oog niets om
 * schaal aan af te lezen: pas als beton korrel en vlekken heeft, wordt het
 * *vloer* in plaats van *grijs vlak*.
 *
 * Alle maps zijn naadloos tileable. De ruis werkt op een periodiek rooster —
 * elke octaaf verdubbelt de periode, dus elke octaaf wrapt op dezelfde rand.
 * Dat is de reden dat hier waarderuis staat en geen simplex: simplex is mooier
 * maar niet zonder meer periodiek te maken.
 *
 * De basiskleuren blijven in `scene/config.ts` staan. Wat hier gemaakt wordt is
 * grijswaarde-detail rond 1.0, dat Three vermenigvuldigt met `material.color`.
 * Zo blijft de Studio de baas over de kleur en gaat dit bestand alleen over
 * structuur.
 */
import * as THREE from 'three'

/* ---------- ruis ---------- */

/** Hash van een roosterpunt naar [0,1). Integer-only, dus stabiel over runs. */
function hash2(ix: number, iy: number, seed: number): number {
  let h = Math.imul(ix, 374761393) + Math.imul(iy, 668265263) + Math.imul(seed, 1274126177)
  h = Math.imul(h ^ (h >>> 13), 1274126177)
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296
}

const gladder = (t: number) => t * t * (3 - 2 * t)

/**
 * Waarderuis met een aparte periode per as. De anisotropie is geen luxe: hout
 * en geborsteld staal zijn juist herkenbaar aan het verschil tussen de assen —
 * isotrope ruis leest altijd als "vuil", nooit als nerf of borstelrichting.
 */
function ruis(u: number, v: number, perX: number, perY: number, seed: number): number {
  const x = u * perX
  const y = v * perY
  // `| 0` in plaats van Math.floor: u en v liggen in [0,1) en de periodes zijn
  // positief, dus afkappen en afronden-naar-beneden zijn hier hetzelfde.
  const x0 = x | 0
  const y0 = y | 0
  const fx = gladder(x - x0)
  const fy = gladder(y - y0)
  // Alleen de buur kan buiten het rooster vallen, en dan precies één stap.
  // Een volledige modulo (twee delingen per as per aanroep) is hier verspilling:
  // deze functie draait dertien keer per pixel, een miljoen pixels lang.
  const x1 = x0 + 1 === perX ? 0 : x0 + 1
  const y1 = y0 + 1 === perY ? 0 : y0 + 1
  const a = hash2(x0, y0, seed)
  const b = hash2(x1, y0, seed)
  const c = hash2(x0, y1, seed)
  const d = hash2(x1, y1, seed)
  const boven = a + (b - a) * fx
  const onder = c + (d - c) * fx
  return boven + (onder - boven) * fy
}

/** Gestapelde octaven. `perX`/`perY` verdubbelen mee, dus het blijft tileable. */
function fbm(
  u: number, v: number, perX: number, perY: number, seed: number,
  octaven = 4, winst = 0.5,
): number {
  let som = 0
  let amp = 1
  let norm = 0
  let px = perX
  let py = perY
  for (let i = 0; i < octaven; i++) {
    som += amp * ruis(u, v, px, py, seed + i * 1013)
    norm += amp
    amp *= winst
    px *= 2
    py *= 2
  }
  return som / norm
}

/**
 * Cellulaire ruis (Worley). Schrijft in `uit`: [0] de afstand tot het
 * dichtstbijzijnde punt, [1] die tot het op één na dichtstbijzijnde.
 *
 * Dit is het primitief voor scheuren en noesten, en geribde ruis is dat niet.
 * `F2 - F1` is nul precies op de grens tussen twee cellen en loopt daarvandaan
 * op — dat levert een aaneengesloten net van lijnen op, zoals krimpscheuren in
 * een betonvloer die echt langs celranden lopen. `F1` alleen is klein in het
 * midden van een cel, en dat is een noest.
 *
 * Tileable doordat de hash over de periode wrapt; de afstand rekent wél met de
 * onverschoven celcoördinaat, anders zou een punt net over de rand naar de
 * verkeerde kant springen.
 */
function worley(u: number, v: number, per: number, seed: number, uit: Float64Array): void {
  const x = u * per
  const y = v * per
  const cx = x | 0
  const cy = y | 0
  let f1 = 9
  let f2 = 9

  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const gx = cx + dx
      const gy = cy + dy
      const hx = ((gx % per) + per) % per
      const hy = ((gy % per) + per) % per
      const px = gx + hash2(hx, hy, seed)
      const py = gy + hash2(hx, hy, seed + 7919)
      const ax = px - x
      const ay = py - y
      const d = Math.sqrt(ax * ax + ay * ay)
      if (d < f1) { f2 = f1; f1 = d } else if (d < f2) { f2 = d }
    }
  }

  uit[0] = f1
  uit[1] = f2
}

const klem = (x: number, a = 0, b = 1) => (x < a ? a : x > b ? b : x)

/** Zachte drempel; `breedte` 0 zou een harde rand geven en dus aliasing. */
function drempel(x: number, rand: number, breedte: number): number {
  return klem((x - rand) / Math.max(breedte, 1e-4))
}

/* ---------- textuurfabriek ---------- */

/**
 * TypeScript 5.7 maakt onderscheid tussen `ArrayBuffer` en `SharedArrayBuffer`
 * als drager van een typed array, en `THREE.DataTexture` accepteert alleen de
 * eerste. Vandaar deze alias plus de expliciete buffer: `new Uint8Array(n)`
 * levert `Uint8Array<ArrayBufferLike>` op en dat past niet.
 */
type Bytes = Uint8Array<ArrayBuffer>

const bytes = (n: number): Bytes => new Uint8Array(new ArrayBuffer(n))

function texturen2D(data: Bytes, maat: number, srgb: boolean): THREE.DataTexture {
  const t = new THREE.DataTexture(data, maat, maat, THREE.RGBAFormat)
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace
  t.wrapS = THREE.RepeatWrapping
  t.wrapT = THREE.RepeatWrapping
  t.generateMipmaps = true
  t.minFilter = THREE.LinearMipmapLinearFilter
  t.magFilter = THREE.LinearFilter
  t.needsUpdate = true
  return t
}

/**
 * Normal map uit een hoogteveld, via centrale differentie met wrap.
 *
 * Zonder wrap zou de naad een zichtbare richel worden: precies op de tegelrand
 * staat dan een verkeerde helling, en die herhaalt zich over de hele vloer.
 */
function normaalUitHoogte(hoogte: Float32Array, maat: number, sterkte: number): THREE.DataTexture {
  const data = bytes(maat * maat * 4)
  const op = (x: number, y: number) => hoogte[((y + maat) % maat) * maat + ((x + maat) % maat)]

  for (let y = 0; y < maat; y++) {
    for (let x = 0; x < maat; x++) {
      const dx = (op(x + 1, y) - op(x - 1, y)) * sterkte
      const dy = (op(x, y + 1) - op(x, y - 1)) * sterkte
      // Normaal van het hoogteveld: (-dx, -dy, 1), genormaliseerd naar 0..255.
      const len = Math.hypot(dx, dy, 1)
      const i = (y * maat + x) * 4
      data[i] = ((-dx / len) * 0.5 + 0.5) * 255
      data[i + 1] = ((-dy / len) * 0.5 + 0.5) * 255
      data[i + 2] = (1 / len) * 0.5 * 255 + 127.5
      data[i + 3] = 255
    }
  }
  return texturen2D(data, maat, false)
}

export interface Oppervlak {
  map: THREE.DataTexture
  roughnessMap: THREE.DataTexture
  normalMap: THREE.DataTexture
}

/**
 * Bouwt de drie maps in één pixellus.
 *
 * `verf` schrijft grijswaarde, ruwheid en hoogte in `uit` in plaats van een
 * tuple terug te geven. Dat leest minder prettig, en het is de reden dat deze
 * generator bruikbaar is: een tuple per pixel is bij 1024² een miljoen arrays
 * die de GC direct weer mag opruimen, midden in de laadtijd van de kiosk.
 *
 * Drie losse lussen zou leesbaarder zijn maar drie keer zo duur.
 */
type Verf = (u: number, v: number, uit: Float64Array) => void

function maak(maat: number, normaalSterkte: number, verf: Verf): Oppervlak {
  const kleur = bytes(maat * maat * 4)
  const ruw = bytes(maat * maat * 4)
  const hoogte = new Float32Array(maat * maat)
  const uit = new Float64Array(3)

  for (let y = 0; y < maat; y++) {
    const v = y / maat
    for (let x = 0; x < maat; x++) {
      const u = x / maat
      verf(u, v, uit)
      const g = uit[0]
      const r = uit[1]
      const h = uit[2]
      const i = (y * maat + x) * 4
      const c = klem(g) * 255
      kleur[i] = c
      kleur[i + 1] = c
      kleur[i + 2] = c
      kleur[i + 3] = 255
      // Three leest ruwheid uit het groene kanaal (glTF-conventie).
      const rr = klem(r) * 255
      ruw[i] = rr
      ruw[i + 1] = rr
      ruw[i + 2] = rr
      ruw[i + 3] = 255
      hoogte[y * maat + x] = h
    }
  }

  return {
    map: texturen2D(kleur, maat, true),
    roughnessMap: texturen2D(ruw, maat, false),
    normalMap: normaalUitHoogte(hoogte, maat, normaalSterkte * maat * 0.02),
  }
}

/* ---------- de materialen ---------- */

/**
 * Gepolijst magazijnbeton: troffelwolken, een zweem toeslagmateriaal,
 * haarscheuren en veegsporen van de vlindermachine.
 *
 * De eerste versie was ruw beton — twee korrelmaten met een flinke amplitude
 * en poriën — en op de afdruk las de vloer als grind of asfalt. Een modern
 * magazijn heeft een monolithisch afgevlinderde vloer: glad, licht glanzend,
 * met grote zachte wolken waar de machine langer of korter heeft gedraaid, en
 * alleen hier en daar een blootliggend steentje. Het detail zit daar niet in
 * de korrel maar in de *glans*: de wolken zijn vooral ruwheidsverschil, en dus
 * zie je ze pas in de spiegeling van de armaturen — precies zoals in het echt.
 *
 * De ruwheid varieert niet alleen, ze doet méér dan de kleur. Een gladgereden
 * of bemorste plek maakt beton glanzender, niet matter, dus zo'n plek vangt het
 * licht van de armaturen anders dan de rest van de vloer.
 */
export function beton(maat: number, seed: number): Oppervlak {
  const cel = new Float64Array(2)

  // Normaalsterkte 0,08: een gevlinderde vloer is optisch vlak. Met reliëf in
  // de korrel spiegelde elke korrel apart de armaturen, en op de proefafdruk
  // lag de vloer vol witte vonken — glitter in plaats van glans.
  return maak(maat, 0.08, (u, v, uit) => {
    const fijn = fbm(u, v, 96, 96, seed, 2)
    const grind = fbm(u, v, 32, 32, seed + 11, 2)
    // Troffelwolken: de grootste structuur in de tegel. Periode 2 op 3 m, dus
    // wolken van anderhalve meter — groter en ze verraden de herhaling, want
    // wat groter is dan de tegel hoort in `Vloer.tsx` in wereldcoördinaten.
    const wolk = fbm(u, v, 2, 2, seed + 29, 3)
    // Veegsporen: anisotroop, zoals de bladen van de vlinder over de vloer gaan.
    const veeg = fbm(u, v, 14, 3, seed + 41, 2)

    // Haarscheuren: dunner en zeldzamer dan in de eerste versie. Periode 2 op
    // een tegel van 3 m geeft cellen van anderhalve meter; de krimpvoegen op
    // 6 m staan al als geometrie in `Vloer.tsx`.
    worley(u, v, 2, seed + 47, cel)
    // Niet overal: een scheurnet dat over de hele vloer doorloopt leest als
    // tegels. Een ruismasker laat de scheuren hier en daar opduiken en
    // weer uitlopen, zoals haarscheuren in een echte vloer doen.
    const masker = drempel(fbm(u, v, 3, 3, seed + 90, 2), 0.52, 0.16)
    const scheur = (1 - drempel(cel[1] - cel[0], 0.0, 0.006)) * masker

    // Een enkel blootliggend steentje, lichter dan de cementhuid.
    const steen = hash2((u * maat * 0.5) | 0, (v * maat * 0.5) | 0, seed + 71) > 0.9994 ? 1 : 0

    // Ruime marge rond 1,0 — de vloerkleur is donker, en een map die tussen
    // 0,85 en 0,95 blijft levert op zo'n ondergrond drie eenheden verschil op
    // 8 bits op: onzichtbaar.
    let g = 0.9 + (wolk - 0.5) * 0.3 + (veeg - 0.5) * 0.08 + (fijn - 0.5) * 0.1 + (grind - 0.5) * 0.08
    g -= scheur * 0.09
    g += steen * 0.28

    // Glans: gepolijst is ~0,25, een doffe wolk ~0,6.
    let r = 0.4 + (wolk - 0.5) * 0.42 + (veeg - 0.5) * 0.18 + (fijn - 0.5) * 0.06
    r += scheur * 0.12 + steen * 0.2

    uit[0] = g
    uit[1] = r
    // Hoogte alleen uit wat je voelt als je eroverheen loopt: de wolken en de
    // scheuren. De korrel zit in kleur en glans, niet in het reliëf.
    uit[2] = wolk * 0.12 + veeg * 0.04 - scheur * 0.5
  })
}

/**
 * Gepoedercoat constructiestaal: sinaasappelhuid, borstelrichting en slijtage.
 *
 * De periodes lopen bewust ver uiteen per as (240 tegen 8): dat is wat een
 * krasrichting is. Isotroop zou dit stof zijn in plaats van gebruikssporen.
 */
export function staal(maat: number, seed: number): Oppervlak {
  return maak(maat, 0.55, (u, v, uit) => {
    const huid = fbm(u, v, 40, 40, seed, 3)
    const kras = fbm(u, v, 240, 8, seed + 13, 3)
    const vuil = fbm(u, v, 5, 5, seed + 37, 3)

    const krasLijn = drempel(kras, 0.72, 0.1)

    let g = 0.94 + (huid - 0.5) * 0.1
    g -= drempel(vuil, 0.6, 0.3) * 0.12
    g += krasLijn * 0.06 // een kras is lichter: blank metaal onder de coating

    let r = 0.5 + (huid - 0.5) * 0.16 + (vuil - 0.5) * 0.12
    r -= krasLijn * 0.18

    uit[0] = g
    uit[1] = r
    uit[2] = huid * 0.6 + kras * 0.25
  })
}

/**
 * Golfkarton: vezel, de ribben van de golf en scheuren aan de randen.
 *
 * De golf is een sinus, geen ruis — een echte golfplaat is regelmatig, en juist
 * die regelmaat naast de onregelmatige vezel maakt dat het als karton leest.
 */
export function karton(maat: number, seed: number): Oppervlak {
  return maak(maat, 0.3, (u, v, uit) => {
    const vezel = fbm(u, v, 200, 60, seed, 3)
    // De golf van de golfplaat, maar nauwelijks. In de eerste versie stond deze
    // op 24 ribbels met een hoogte-aandeel van 0,35, en dat is precies verkeerd
    // om: bij een echte doos zie je de golf alleen op de gesneden rand, nooit
    // op het bedrukte vlak. Op de proefafdruk werden de stapels daardoor
    // golfplaat in plaats van karton. Wat overblijft is de lichte rilling die
    // je op een doos inderdaad ziet als het licht er schuin op valt.
    const golf = Math.sin(v * Math.PI * 2 * 44) * 0.5 + 0.5
    const vlek = fbm(u, v, 4, 4, seed + 19, 3)
    const schuur = fbm(u, v, 22, 22, seed + 53, 2)

    let g = 0.93 + (vezel - 0.5) * 0.24 + (golf - 0.5) * 0.015
    g -= drempel(vlek, 0.66, 0.22) * 0.12
    g -= drempel(schuur, 0.74, 0.14) * 0.09

    const r = 0.9 + (vezel - 0.5) * 0.1 - drempel(vlek, 0.7, 0.2) * 0.1

    uit[0] = g
    uit[1] = r
    uit[2] = golf * 0.05 + vezel * 0.6 + schuur * 0.25
  })
}

/**
 * Pallethout: nerf, jaarringen en noesten.
 *
 * Komt goed uit op de samengestelde palletgeometrie: elke plank en elke klos
 * krijgt in `BoxGeometry` zijn eigen 0..1-UV, dus elk deel krijgt zijn eigen
 * stuk nerf in plaats van dat de nerf over de hele pallet doorloopt. Dat is
 * precies hoe een echte pallet in elkaar zit — losse planken uit losse stammen.
 */
export function hout(maat: number, seed: number): Oppervlak {
  const cel = new Float64Array(2)

  // 0,45 en niet 0,7: op de eerste proefafdruk lazen de kisten als boomschors.
  // Pallethout is geschaafd vuren — de nerf zit in de kleur, niet in het profiel.
  return maak(maat, 0.45, (u, v, uit) => {
    // Jaarringen: een sinus die door ruis vervormd wordt, anders is het
    // streepjespapier.
    // Golving 1,1 en niet 2,4: bij die eerste waarde slingerden de ringen zo
    // ver dat het als marmerdoorloop las in plaats van als nerf. Geschaafd
    // vuren heeft rechte ringen met een lichte slinger, niet omgekeerd.
    const golving = fbm(u, v, 5, 3, seed + 7, 3)
    const ring = Math.sin((u * 16 + golving * 1.1) * Math.PI * 2) * 0.5 + 0.5
    const nerf = fbm(u, v, 240, 10, seed + 23, 3)

    // Noesten: de kern van een cel, niet elke cel er één. De hash op de cel
    // beslist of hier een tak zat — anders staat een pallet vol noesten en
    // leest hij als luipaardvel.
    worley(u, v, 4, seed + 61, cel)
    const heeftNoest = hash2((u * 4) | 0, (v * 4) | 0, seed + 97) > 0.72 ? 1 : 0
    const noest = heeftNoest * (1 - drempel(cel[0], 0.06, 0.13))

    const slijt = fbm(u, v, 9, 9, seed + 83, 3)

    let g = 0.93 + (ring - 0.5) * 0.3 + (nerf - 0.5) * 0.3
    g -= noest * 0.45
    g -= drempel(slijt, 0.68, 0.2) * 0.1

    const r = 0.92 + (ring - 0.5) * 0.06 + noest * 0.05

    uit[0] = g
    uit[1] = r
    uit[2] = ring * 0.22 + nerf * 0.5 - noest * 0.75
  })
}

/**
 * Stellingstaander: gepoedercoat omegaprofiel met twee rijen druppelvormige
 * sleuven, zoals elke palletstelling ze heeft om de liggers op te hangen.
 *
 * Eén tegel is 110 mm breed (de staander) en 100 mm hoog: twee sleuven van de
 * steek van 50 mm. De sleuf is een gat, dus donker, mat en diep in de normal
 * map; daarnaast loopt de vouw van het profiel als een lichte richel. Dit is
 * het detail dat een staander van een balk onderscheidt zodra de camera in een
 * gangpad staat — en in de close-ups van de hotspots staat hij op twee meter.
 */
export function staander(maat: number, seed: number): Oppervlak {
  return maak(maat, 0.9, (u, v, uit) => {
    const huid = fbm(u, v, 16, 16, seed, 3)
    const vuil = fbm(u, v, 4, 1, seed + 5, 2)

    // Druppelsleuf: breder bovenin, smal onderin.
    let gat = 0
    for (const uc of [0.28, 0.72]) {
      for (const vc of [0.25, 0.75]) {
        const dv = (v - vc) / 0.15
        if (dv < -1 || dv > 1) continue
        const breedte = 0.045 * (0.55 + 0.45 * (dv + 1) / 2) * Math.sqrt(Math.max(0, 1 - dv * dv))
        gat = Math.max(gat, 1 - drempel(Math.abs(u - uc), breedte - 0.006, 0.012))
      }
    }
    // Vouwen van het omegaprofiel: een richel in het midden, zijkanten die
    // wegbuigen (donkerder, want ze krijgen minder licht van voren).
    const richel = Math.exp(-Math.pow((u - 0.5) / 0.035, 2))
    const zijkant = drempel(Math.abs(u - 0.5), 0.42, 0.07)

    let g = 0.95 + (huid - 0.5) * 0.08 - zijkant * 0.18 + richel * 0.06
    g -= drempel(vuil, 0.6, 0.3) * 0.08
    g = g * (1 - gat) + 0.06 * gat

    const r = (0.42 + (huid - 0.5) * 0.12) * (1 - gat) + 0.95 * gat

    uit[0] = g
    uit[1] = r
    uit[2] = huid * 0.1 + richel * 0.35 - zijkant * 0.4 - gat * 1.2
  })
}

/**
 * Verzenddoos: het karton van hierboven, plus een strook plakband over het
 * midden en een gedrukt "deze kant boven"-teken. De UV's van een doos lopen
 * per zijvlak van 0 tot 1, dus de tape komt vanzelf midden over boven-, voor-
 * en achtervlak — precies waar hij bij een dichtgeplakte doos ook zit.
 */
export function doos(maat: number, seed: number): Oppervlak {
  const basis = karton(maat, seed)
  const kleur = basis.map.image.data as Uint8Array
  const ruw = basis.roughnessMap.image.data as Uint8Array
  basis.map.dispose(); basis.roughnessMap.dispose(); basis.normalMap.dispose()

  const hoogte = new Float32Array(maat * maat)
  for (let y = 0; y < maat; y++) {
    const v = y / maat
    for (let x = 0; x < maat; x++) {
      const u = x / maat
      const i = (y * maat + x) * 4
      // Plakband: iets lichter, veel gladder, met een rafelig afgescheurd eind
      // is hier niet nodig — de tape loopt door over de rand.
      const tape = 1 - drempel(Math.abs(u - 0.5), 0.07, 0.006)
      const rimpel = fbm(u, v, 4, 40, seed + 3, 2)
      // Pijlen linksboven, twee naast elkaar.
      let pijl = 0
      for (const pu of [0.12, 0.2]) {
        const dx = Math.abs(u - pu)
        const steel = dx < 0.012 && v > 0.1 && v < 0.2 ? 1 : 0
        const punt = v > 0.05 && v < 0.11 && dx < (v - 0.05) * 0.5 ? 1 : 0
        pijl = Math.max(pijl, steel, punt)
      }
      let g = kleur[i] / 255
      g = g * (1 - tape) + (g * 1.08 + 0.04 + (rimpel - 0.5) * 0.06) * tape
      g *= 1 - pijl * 0.55
      const c = klem(g) * 255
      kleur[i] = kleur[i + 1] = kleur[i + 2] = c
      const r = (ruw[i] / 255) * (1 - tape) + (0.3 + (rimpel - 0.5) * 0.15) * tape
      ruw[i] = ruw[i + 1] = ruw[i + 2] = klem(r) * 255
      hoogte[y * maat + x] = (kleur[i] / 255) * 0.3 + tape * 0.2 + rimpel * tape * 0.15
    }
  }
  return {
    map: texturen2D(kleur as Bytes, maat, true),
    roughnessMap: texturen2D(ruw as Bytes, maat, false),
    normalMap: normaalUitHoogte(hoogte, maat, 0.3 * maat * 0.02),
  }
}

/**
 * Wand- en plafondpaneel: sandwichpanelen met naden en lichte deuken.
 *
 * De naad is een harde lijn op vaste afstand; de deuk is laagfrequent. Samen is
 * dat wat een grote wand op diepte houdt in plaats van hem als gat te laten
 * lezen — een vlakke donkere wand geeft de mist niets om op af te tekenen.
 */
export function paneel(maat: number, seed: number): Oppervlak {
  return maak(maat, 0.45, (u, v, uit) => {
    const naad = Math.abs(((u * 6) % 1) - 0.5) // 0 op de naad, 0.5 ertussen
    const naadLijn = 1 - drempel(naad, 0.012, 0.01)
    const deuk = fbm(u, v, 7, 4, seed, 3)
    const vuil = fbm(u, v, 3, 3, seed + 17, 3)

    let g = 0.96 + (deuk - 0.5) * 0.09
    g -= naadLijn * 0.22
    g -= drempel(vuil, 0.62, 0.3) * 0.1

    const r = 0.72 + (deuk - 0.5) * 0.1 + naadLijn * 0.12

    uit[0] = g
    uit[1] = r
    uit[2] = deuk * 0.5 - naadLijn * 1.4
  })
}

/* ---------- vloerdecals ---------- */

/**
 * Losse texturen voor wat óp de vloer ligt: versleten verf, bandensporen en
 * olievlekken. Klein (hooguit 256 breed) en eenmalig, want ze worden in de
 * wereld vele malen herhaald en geschaald; de resolutie zit in de herhaling.
 */
function decal(breedte: number, hoogte: number, verf: (u: number, v: number) => [number, number]) {
  const data = bytes(breedte * hoogte * 4)
  for (let y = 0; y < hoogte; y++) {
    for (let x = 0; x < breedte; x++) {
      const [g, a] = verf(x / breedte, y / hoogte)
      const i = (y * breedte + x) * 4
      const c = klem(g) * 255
      data[i] = c; data[i + 1] = c; data[i + 2] = c
      data[i + 3] = klem(a) * 255
    }
  }
  const t = new THREE.DataTexture(data, breedte, hoogte, THREE.RGBAFormat)
  t.wrapS = THREE.RepeatWrapping
  t.wrapT = THREE.ClampToEdgeWrapping
  t.generateMipmaps = true
  t.minFilter = THREE.LinearMipmapLinearFilter
  t.magFilter = THREE.LinearFilter
  t.colorSpace = THREE.SRGBColorSpace
  t.needsUpdate = true
  return t
}

/**
 * Wikkelfolie: horizontale banen waar de wikkelaar overlapt, met plooien en
 * een lichtere rand op elke overlap. Als alfa, want folie is vooral dáár
 * zichtbaar waar hij dubbel ligt.
 */
export function folie(seed: number) {
  return decal(64, 256, (u, v) => {
    const baan = (v * 7.3) % 1
    const overlap = Math.exp(-Math.pow((baan - 0.08) * 22, 2))
    const plooi = fbm(u, v, 8, 32, seed, 3)
    const a = 0.2 + overlap * 0.45 + (plooi - 0.5) * 0.25
    return [0.85 + overlap * 0.15, a]
  })
}

/**
 * Belijningsverf na een paar jaar heftruckverkeer: afgebladderd waar de wielen
 * eroverheen gaan, rafelig aan de rand, met vuil in de nerf. Eén tegel is 2 m
 * lijn; `u` loopt in de lengte, `v` dwars.
 */
export function verfSlijtage(seed: number) {
  return decal(256, 32, (u, v) => {
    const vlek = fbm(u, v, 16, 2, seed, 3)
    const kras = fbm(u, v, 64, 4, seed + 5, 2)
    const rand = fbm(u, v, 48, 1, seed + 9, 2)
    // Rafelige randen: de lijn is niet overal even breed.
    const dwars = Math.abs(v - 0.5) * 2
    const binnen = 1 - drempel(dwars, 0.78 + (rand - 0.5) * 0.3, 0.08)
    const af = drempel(vlek, 0.62, 0.08) * 0.85 + drempel(kras, 0.7, 0.05) * 0.4
    const a = binnen * (1 - klem(af)) * 0.92
    const g = 0.86 + (vlek - 0.5) * 0.25 - drempel(kras, 0.55, 0.2) * 0.15
    return [g, a]
  })
}

/**
 * Bandensporen: donkere, iets glanzende banen van rubber en vuil, in de lengte
 * gestreept. Dwars zacht uitlopend, zodat een spoor nergens een rand heeft.
 */
export function bandensporen(seed: number) {
  return decal(256, 64, (u, v) => {
    const streep = fbm(u, v, 6, 24, seed, 3)
    const vlek = fbm(u, v, 3, 2, seed + 3, 3)
    const dwars = Math.abs(v - 0.5) * 2
    const profiel = Math.pow(1 - klem(dwars), 1.6)
    // Twee wielsporen per baan, niet één brede veeg.
    const wielen = Math.pow(Math.abs(Math.sin(v * Math.PI * 2)), 0.6)
    const a = profiel * (0.35 + 0.65 * streep) * (0.55 + 0.45 * vlek) * (0.4 + 0.6 * wielen)
    return [0.6, a]
  })
}

/** Een olie- of koffievlek: grillige rand, donkere kern, kringen van indroging. */
export function olievlek(seed: number) {
  const n = 128
  const data = bytes(n * n * 4)
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const u = x / n
      const v = y / n
      const dx = u - 0.5
      const dy = v - 0.5
      const hoek = Math.atan2(dy, dx)
      const r = Math.hypot(dx, dy) * 2
      // De rand golft met de hoek, zodat het geen cirkel is.
      const golf = fbm(Math.cos(hoek) * 0.5 + 0.5, Math.sin(hoek) * 0.5 + 0.5, 4, 4, seed, 3)
      const grens = 0.55 + (golf - 0.5) * 0.5
      const kern = 1 - drempel(r, grens * 0.3, grens * 0.8)
      const kring = Math.exp(-Math.pow((r - grens * 0.92) * 18, 2)) * 0.5
      const binnen = 1 - drempel(r, grens, 0.05)
      const spat = fbm(u, v, 16, 16, seed + 7, 2)
      const a = klem((kern * 0.7 + kring + 0.25) * binnen * (0.7 + 0.5 * spat))
      const i = (y * n + x) * 4
      data[i] = data[i + 1] = data[i + 2] = 40
      data[i + 3] = a * 255
    }
  }
  const t = new THREE.DataTexture(data, n, n, THREE.RGBAFormat)
  t.generateMipmaps = true
  t.minFilter = THREE.LinearMipmapLinearFilter
  t.magFilter = THREE.LinearFilter
  t.needsUpdate = true
  return t
}

/**
 * Draadgaas voor rolcontainers: een raster van draden als alfa, voor
 * `alphaTest`. Three neemt `alphaTest` en de map mee in het schaduwmateriaal,
 * dus het gaas werpt ook een gaasschaduw — geen massief blok.
 */
export function gaas() {
  const n = 64
  const data = bytes(n * n * 4)
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const i = (y * n + x) * 4
      const draad = x % 16 < 2 || y % 16 < 2 ? 1 : 0
      data[i] = data[i + 1] = data[i + 2] = 200
      data[i + 3] = draad * 255
    }
  }
  const t = new THREE.DataTexture(data, n, n, THREE.RGBAFormat)
  t.wrapS = THREE.RepeatWrapping
  t.wrapT = THREE.RepeatWrapping
  t.generateMipmaps = true
  t.minFilter = THREE.LinearMipmapLinearFilter
  t.magFilter = THREE.LinearFilter
  t.needsUpdate = true
  return t
}

/* ---------- cache ---------- */

export interface Oppervlakken {
  beton: Oppervlak
  staal: Oppervlak
  karton: Oppervlak
  hout: Oppervlak
  paneel: Oppervlak
  staander: Oppervlak
  doos: Oppervlak
}

let cache: { sleutel: string; set: Oppervlakken } | null = null

/**
 * Afronden naar een macht van twee, tussen 128 en 2048.
 *
 * De schuif in de Studio loopt in stappen van 256 en kan dus op 768 of 1280
 * blijven staan. WebGL2 kan daar mipmaps van maken, maar niet elke driver doet
 * dat even goed, en een vloertextuur zonder werkende mipmaps flikkert in de
 * verte. Liever hier afronden dan op de beursvloer ontdekken dat één stand van
 * één schuif ruis geeft.
 */
function machtVanTwee(n: number): number {
  const geklemd = Math.min(2048, Math.max(128, n || 1024))
  return 2 ** Math.round(Math.log2(geklemd))
}

/**
 * Eén set per (detail, seed). De kiosk draait maanden op dezelfde set, dus er
 * wordt niet opgeruimd zolang de sleutel gelijk blijft; wisselt hij — alleen in
 * de Studio — dan gaat de oude set eerst weg, want anders lekt elke schuifbeweging
 * een paar megabyte VRAM.
 */
export function oppervlakken(ruwDetail: number, seed: number): Oppervlakken {
  const detail = machtVanTwee(ruwDetail)
  const sleutel = `${detail}:${seed}`
  if (cache && cache.sleutel === sleutel) return cache.set

  if (cache) {
    for (const o of Object.values(cache.set)) {
      o.map.dispose()
      o.roughnessMap.dispose()
      o.normalMap.dispose()
    }
  }

  // De vloer is het grootste aaneengesloten vlak en krijgt daarom de hoogste
  // resolutie; de rest is altijd op afstand of klein in beeld.
  const groot = detail
  const klein = Math.max(128, detail / 2)

  const set: Oppervlakken = {
    beton: beton(groot, seed),
    staal: staal(klein, seed + 101),
    karton: karton(klein, seed + 211),
    hout: hout(klein, seed + 307),
    paneel: paneel(klein, seed + 401),
    // De staandertegel is 11 × 10 cm: 128 pixels is al bijna een millimeter
    // per texel, meer ziet niemand.
    staander: staander(128, seed + 503),
    doos: doos(klein, seed + 211),
  }

  cache = { sleutel, set }
  return set
}

/**
 * Anisotropie kan pas als er een renderer is, en is op een vloer die je onder
 * een scherpe hoek ziet geen detailkwestie maar een ruiskwestie: zonder dit
 * flikkert de betonkorrel in de verte bij elke camerabeweging.
 */
export function zetAnisotropie(set: Oppervlakken, max: number) {
  for (const o of Object.values(set)) {
    for (const t of [o.map, o.roughnessMap, o.normalMap]) {
      if (t.anisotropy !== max) {
        t.anisotropy = max
        t.needsUpdate = true
      }
    }
  }
}
